import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "ui";

import { readBillingProductSelection } from "src/helpers/billingSelection";
import { useCreateCheckoutSession } from "src/mutations/useCreateCheckoutSession";
import { useStartTrial } from "src/mutations/useStartTrial";
import { type BillingProduct } from "src/queries/billing";

import { BillingIntervalToggle } from "./BillingIntervalToggle";
import { BillingProductPicker } from "./BillingProductPicker";
import { PricingProductCard } from "./PricingProductCard";

interface Props {
  products: BillingProduct[];
  eligibleForTrial: boolean;
  variant?: "full" | "compact";
  initialBillingProductId?: string | null;
}

export function SubscribeCheckout(props: Props) {
  const { t } = useTranslation();
  const startTrial = useStartTrial();
  const createCheckoutSession = useCreateCheckoutSession();
  const [interval, setInterval] = useState<"MONTH" | "YEAR">("MONTH");
  const [selectedBillingProductId, setSelectedBillingProductId] = useState(
    props.initialBillingProductId ??
      props.products.find((product) => product.featured)?.id ??
      props.products[0]?.id ??
      null,
  );

  const hasInitialBillingProductId = Boolean(props.initialBillingProductId);
  useEffect(() => {
    if (hasInitialBillingProductId) {
      return;
    }

    const selection = readBillingProductSelection();

    if (!selection) {
      return;
    }

    setSelectedBillingProductId(selection.billingProductId);
    setInterval(selection.interval);
  }, [hasInitialBillingProductId]);

  const pending = startTrial.isPending || createCheckoutSession.isPending;

  const selectedProduct = props.products.find(
    (product) => product.id === selectedBillingProductId,
  );
  const selectedPrice = selectedProduct?.prices.find(
    (price) => price.interval === interval,
  );

  function handleProductSelect(product: BillingProduct) {
    if (props.eligibleForTrial) {
      startTrial.mutate({ billingProductId: product.id });
      return;
    }

    const price = product.prices.find(
      (candidate) => candidate.interval === interval,
    );

    if (!price) {
      return;
    }

    createCheckoutSession.mutate({ priceId: price.id });
  }

  function isProductPending(product: BillingProduct) {
    if (startTrial.isPending) {
      return startTrial.variables?.billingProductId === product.id;
    }

    return product.prices.some(
      (price) => price.id === createCheckoutSession.variables?.priceId,
    );
  }

  if (props.variant === "full") {
    return (
      <div className="space-y-8">
        <div className="flex justify-center">
          <BillingIntervalToggle value={interval} onChange={setInterval} />
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {props.products.map((product) => {
            return (
              <PricingProductCard
                key={product.id}
                product={product}
                interval={interval}
                ctaLabel={
                  props.eligibleForTrial
                    ? undefined
                    : t("planChooser.choosePlan", { plan: product.name })
                }
                ctaLoading={pending && isProductPending(product)}
                ctaDisabled={pending}
                trialNote={
                  props.eligibleForTrial
                    ? t("pricing.noCreditCardRequired")
                    : undefined
                }
                onSelect={handleProductSelect}
              />
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-center">
        <BillingIntervalToggle value={interval} onChange={setInterval} />
      </div>
      <BillingProductPicker
        products={props.products}
        interval={interval}
        selectedPriceId={selectedPrice?.id ?? null}
        onSelect={(_price, product) => {
          setSelectedBillingProductId(product.id);
        }}
      />
      {props.eligibleForTrial ? (
        <div>
          <p className="mb-2 text-center text-xs text-gray-500">
            {t("pricing.noCreditCardRequired")}
          </p>
          <Button
            className="w-full"
            loading={startTrial.isPending}
            disabled={!selectedProduct || pending}
            onClick={() => {
              if (!selectedProduct) {
                return;
              }

              startTrial.mutate({ billingProductId: selectedProduct.id });
            }}
          >
            {t("planChooser.trialCta")}
          </Button>
        </div>
      ) : (
        <Button
          className="w-full"
          loading={createCheckoutSession.isPending}
          disabled={!selectedPrice || pending}
          onClick={() => {
            if (!selectedPrice) {
              return;
            }

            createCheckoutSession.mutate({ priceId: selectedPrice.id });
          }}
        >
          {t("subscribeCheckout.continueCta")}
        </Button>
      )}
    </div>
  );
}
